import type { ProductOptionGroupView, ProductView } from "@/lib/api/types";

export const SIZE_GROUP_NAMES = [
  "size",
  "sizes",
  "المقاس",
  "المقاسات",
  "مقاس",
  "حجم",
] as const;

export const COLOR_GROUP_NAMES = [
  "color",
  "colors",
  "colour",
  "colours",
  "اللون",
  "الألوان",
  "لون",
  "ألوان",
] as const;

export function matchGroup(
  group: Pick<ProductOptionGroupView, "nameAr" | "nameEn">,
  candidates: readonly string[],
): boolean {
  const ar = group.nameAr?.trim().toLowerCase() ?? "";
  const en = group.nameEn?.trim().toLowerCase() ?? "";
  return candidates.some((c) => c === ar || c === en);
}

export function findSizeGroup(product: ProductView): ProductOptionGroupView | null {
  return product.optionGroups.find((g) => matchGroup(g, SIZE_GROUP_NAMES)) ?? null;
}

export function findColorGroup(product: ProductView): ProductOptionGroupView | null {
  return product.optionGroups.find((g) => matchGroup(g, COLOR_GROUP_NAMES)) ?? null;
}

/**
 * Fallback swatch for well-known colour names when no hex is stored.
 * Keyed by lowercased English *and* Arabic names — sellers label colours in
 * either language, so a swatch should resolve for both. Keep additions here in
 * sync rather than scattering colour literals across components.
 */
const NAME_TO_HEX: Record<string, string> = {
  // ── Core ──────────────────────────────────────────────────────────────────
  black: "#000000",
  white: "#ffffff",
  "off-white": "#faf9f6",
  offwhite: "#faf9f6",
  ivory: "#fffff0",
  cream: "#f5ebd6",
  red: "#ef4444",
  blue: "#3b82f6",
  green: "#16a34a",
  yellow: "#eab308",
  orange: "#f97316",
  purple: "#a855f7",
  pink: "#ec4899",
  brown: "#92400e",
  beige: "#e8dcc4",
  gray: "#9ca3af",
  grey: "#9ca3af",
  navy: "#1e3a5f",
  gold: "#d4af37",
  silver: "#c0c0c0",
  // ── Fashion / extended ────────────────────────────────────────────────────
  camel: "#c19a6b",
  burgundy: "#800020",
  wine: "#722f37",
  maroon: "#800000",
  teal: "#14b8a6",
  olive: "#808000",
  mustard: "#e1ad01",
  khaki: "#c3b091",
  charcoal: "#36454f",
  mint: "#98d8c8",
  lavender: "#b57edc",
  lilac: "#c8a2c8",
  mauve: "#e0b0ff",
  coral: "#ff7f50",
  turquoise: "#40e0d0",
  tan: "#d2b48c",
  taupe: "#8b7d6b",
  nude: "#e3bc9a",
  peach: "#ffcba4",
  apricot: "#fbceb1",
  salmon: "#fa8072",
  rust: "#b7410e",
  terracotta: "#e2725b",
  bronze: "#cd7f32",
  copper: "#b87333",
  mocha: "#6f4e37",
  chocolate: "#7b3f00",
  caramel: "#af6e4d",
  stone: "#a8998a",
  sand: "#d6c7a8",
  sage: "#9caf88",
  slate: "#64748b",
  rose: "#e8a3a3",
  onyx: "#1a1a1a",
  emerald: "#10b981",
  forest: "#228b22",
  lime: "#84cc16",
  aqua: "#00ffff",
  sky: "#87ceeb",
  cobalt: "#0047ab",
  royal: "#4169e1",
  indigo: "#4f46e5",
  violet: "#8b5cf6",
  fuchsia: "#d946ef",
  magenta: "#d946ef",
  plum: "#8e4585",
  berry: "#990f4b",
  denim: "#1560bd",
  amber: "#ffbf00",
  lemon: "#fff44f",
  // ── Arabic ────────────────────────────────────────────────────────────────
  أسود: "#000000",
  أبيض: "#ffffff",
  أحمر: "#ef4444",
  أزرق: "#3b82f6",
  أخضر: "#16a34a",
  أصفر: "#eab308",
  برتقالي: "#f97316",
  بنفسجي: "#a855f7",
  وردي: "#ec4899",
  زهري: "#ec4899",
  بني: "#92400e",
  بيج: "#e8dcc4",
  رمادي: "#9ca3af",
  كحلي: "#1e3a5f",
  ذهبي: "#d4af37",
  فضي: "#c0c0c0",
  كاميل: "#c19a6b",
  جملي: "#c19a6b",
  خمري: "#800020",
  نبيتي: "#800020",
  عنابي: "#800000",
  نبيذي: "#722f37",
  بترولي: "#14b8a6",
  زيتي: "#808000",
  تركواز: "#40e0d0",
  فيروزي: "#40e0d0",
  موف: "#e0b0ff",
  موفي: "#e0b0ff",
  لبني: "#add8e6",
  سماوي: "#87ceeb",
  كريمي: "#f5ebd6",
  نحاسي: "#b87333",
  ليموني: "#fff44f",
  مرجاني: "#ff7f50",
  عاجي: "#fffff0",
  فحمي: "#36454f",
  بحري: "#1e3a5f",
};

export function colorHexFromName(name: string): string {
  return NAME_TO_HEX[name.trim().toLowerCase()] ?? "#9ca3af";
}

/**
 * Resolve a swatch colour for an option value: prefer the stored `colorHex`,
 * else derive from the (English then Arabic) name, else a neutral grey.
 */
export function swatchHex(value: {
  colorHex?: string | null;
  valueEn?: string | null;
  valueAr?: string | null;
}): string {
  if (value.colorHex && /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(value.colorHex)) {
    return value.colorHex;
  }
  return colorHexFromName(value.valueEn || value.valueAr || "");
}
